import {
  APP_OPERATOR_HUMANIZED_TIMING_MS,
  type AppOperatorBridgeTarget,
  appOperatorMotionDurationMs,
  type AppOperatorRect,
  type AppOperatorScrollDirection,
  centerOfAppOperatorRect,
  createNaturalAppOperatorPath,
} from './operator-contract';

export interface AIOperatorPresenceSnapshot {
  visible: boolean;
  cursorVisible: boolean;
  clicking: boolean;
  cursor: { x: number; y: number } | null;
  fieldRect: AppOperatorRect | null;
  scrollDirection: AppOperatorScrollDirection | null;
  /** The cursor stays where its last gesture ended; nothing is being shown. */
  resting: boolean;
}

type Listener = () => void;

/** A point in the viewport's CSS pixels. */
export interface AppOperatorPoint {
  x: number;
  y: number;
}

/** The pointer steps of a drag, run by whoever owns the input path. */
export interface AppOperatorDragGesture {
  press(): void;
  move(point: AppOperatorPoint): void;
  release(): void;
}

/** Points an unanimated drag still passes, so the app follows it like a move. */
const INSTANT_DRAG_STEPS = 8;

const pointBetween = (
  from: AppOperatorPoint,
  to: AppOperatorPoint,
  progress: number
): AppOperatorPoint => ({
  x: from.x + (to.x - from.x) * progress,
  y: from.y + (to.y - from.y) * progress,
});

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

export class AIOperatorPresentationStore {
  private readonly listeners = new Set<Listener>();
  private readonly pendingDelayCancels = new Set<() => void>();
  private animationToken = 0;
  private lingers = false;
  private snapshot: AIOperatorPresenceSnapshot = {
    visible: false,
    cursorVisible: false,
    clicking: false,
    cursor: null,
    fieldRect: null,
    scrollDirection: null,
    resting: false,
  };

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): AIOperatorPresenceSnapshot => this.snapshot;

  async presentTarget(
    target: AppOperatorBridgeTarget | undefined
  ): Promise<void> {
    if (!this.paced) {
      return;
    }
    const rect = target?.fieldRect ?? target?.rect;
    if (!rect) {
      const token = this.advanceAnimationToken();
      this.patch({
        visible: true,
        cursorVisible: true,
        fieldRect: null,
        scrollDirection: target?.scrollDirection ?? null,
      });
      await this.sleep(
        APP_OPERATOR_HUMANIZED_TIMING_MS.targetWithoutGeometry,
        token
      );
      return;
    }

    const destination = centerOfAppOperatorRect(rect);
    const source = this.snapshot.cursor ?? {
      x: destination.x - Math.min(120, rect.width + 48),
      y: destination.y - 32,
    };
    const token = await this.animateCursor(source, destination);
    if (token === null || !this.isTokenActive(token)) return;
    if (target?.scrollDirection) {
      this.patch({ scrollDirection: target.scrollDirection });
      await this.sleep(APP_OPERATOR_HUMANIZED_TIMING_MS.scroll, token);
      if (!this.isTokenActive(token)) return;
    }
    if (target?.fieldRect) {
      this.patch({
        visible: true,
        cursorVisible: false,
        fieldRect: target.fieldRect,
        scrollDirection: null,
      });
      return;
    }
    this.patch({
      visible: true,
      cursorVisible: true,
      clicking: true,
      fieldRect: null,
      scrollDirection: null,
    });
    await this.sleep(APP_OPERATOR_HUMANIZED_TIMING_MS.click, token);
    if (!this.isTokenActive(token)) return;
    this.patch({ clicking: false });
  }

  async presentExecutionResult(
    target: AppOperatorBridgeTarget | undefined
  ): Promise<void> {
    if (!this.paced) return;
    const token = this.animationToken;
    if (target?.fieldRect) {
      this.patch({
        visible: true,
        cursorVisible: false,
        fieldRect: target.fieldRect,
        scrollDirection: null,
      });
      await this.sleep(APP_OPERATOR_HUMANIZED_TIMING_MS.fieldResult, token);
      return;
    }
    await this.sleep(APP_OPERATOR_HUMANIZED_TIMING_MS.actionResult, token);
  }

  /** Whether what the AI does takes the time a person would need to follow it. */
  get paced(): boolean {
    return !prefersReducedMotion();
  }

  /**
   * Navigation stays visible even when motion is reduced.
   * The cursor stays until what it activated is done.
   */
  async presentNavigationClick(
    target: AppOperatorBridgeTarget,
    activate: () => void | Promise<void>,
    signal: AbortSignal
  ): Promise<{ clicked: boolean }> {
    if (signal.aborted || !target.rect) return { clicked: false };
    const abort = () => this.clear();
    signal.addEventListener('abort', abort, { once: true });
    try {
      if (!(await this.approach(centerOfAppOperatorRect(target.rect), signal)))
        return { clicked: false };
      const running = activate();
      if (running) {
        // The press is over; the cursor rests there while what it started goes on.
        this.patch({ clicking: false });
        await running;
      }
      return { clicked: true };
    } finally {
      signal.removeEventListener('abort', abort);
      this.rest();
    }
  }

  /**
   * A drag stays visible the whole way: the cursor presses at the start, carries
   * what it holds along its path and lets go at the end. Up to the press it can
   * be called off like a click; once pressed it always ends at its destination,
   * so nothing is left hanging on the pointer.
   */
  async presentNavigationDrag(
    target: AppOperatorBridgeTarget,
    to: AppOperatorPoint,
    drag: AppOperatorDragGesture,
    signal: AbortSignal
  ): Promise<{ clicked: boolean }> {
    if (signal.aborted || !target.rect) return { clicked: false };
    const abort = () => this.clear();
    signal.addEventListener('abort', abort, { once: true });
    try {
      const from = centerOfAppOperatorRect(target.rect);
      if (!(await this.approach(from, signal))) return { clicked: false };
      drag.press();
      signal.removeEventListener('abort', abort);
      try {
        if (!this.paced) {
          for (let step = 1; step < INSTANT_DRAG_STEPS; step++)
            drag.move(pointBetween(from, to, step / INSTANT_DRAG_STEPS));
        } else {
          await this.animateCursor(from, to, undefined, drag.move);
        }
      } finally {
        this.patch({ cursor: to });
        drag.move(to);
        drag.release();
      }
      return { clicked: true };
    } finally {
      signal.removeEventListener('abort', abort);
      this.rest();
    }
  }

  /**
   * Shows the cursor at a place that changed without the app being operated,
   * for `holdMs`. Whatever is presented next takes the cursor over.
   */
  async presentPlace(place: AppOperatorPoint, holdMs: number): Promise<void> {
    let token: number | null;
    if (!this.paced) {
      token = this.advanceAnimationToken();
      this.patch({
        visible: true,
        cursorVisible: true,
        cursor: place,
        clicking: false,
        fieldRect: null,
        scrollDirection: null,
      });
    } else {
      token = await this.animateCursor(
        this.snapshot.cursor ?? { x: place.x - 100, y: place.y - 32 },
        place
      );
    }
    if (token === null || !this.isTokenActive(token)) return;
    await this.sleep(holdMs, token);
    if (this.isTokenActive(token)) this.rest();
  }

  private async approach(
    destination: AppOperatorPoint,
    signal: AbortSignal
  ): Promise<boolean> {
    let token: number | null;
    if (!this.paced) {
      token = this.advanceAnimationToken();
      this.patch({
        visible: true,
        cursorVisible: true,
        cursor: destination,
        clicking: false,
        fieldRect: null,
        scrollDirection: null,
      });
    } else {
      token = await this.animateCursor(
        this.snapshot.cursor ?? {
          x: destination.x - 100,
          y: destination.y - 32,
        },
        destination,
        signal
      );
    }
    if (token === null || signal.aborted || !this.isTokenActive(token))
      return false;
    this.patch({ clicking: true });
    // Leave the actual viewport projection visible for a render before clicking.
    await this.sleep(APP_OPERATOR_HUMANIZED_TIMING_MS.click, token);
    return !signal.aborted && this.isTokenActive(token);
  }

  /** Whether the cursor stays where a gesture ended instead of leaving with it. */
  setLingering(lingers: boolean): void {
    this.lingers = lingers;
    if (!lingers && this.snapshot.resting) this.clear();
  }

  /**
   * What was shown is over. A lingering cursor stays where it ended, without
   * press or field frame; any other leaves.
   */
  rest(): void {
    if (!this.lingers || !this.snapshot.visible || !this.snapshot.cursor) {
      this.clear();
      return;
    }
    this.advanceAnimationToken();
    this.patch({
      cursorVisible: true,
      clicking: false,
      fieldRect: null,
      scrollDirection: null,
      resting: true,
    });
  }

  /**
   * What the resting cursor rests on moved on screen; `null` when it is out of
   * sight, where the cursor is not drawn but keeps resting.
   */
  moveResting(point: AppOperatorPoint | null): void {
    if (this.snapshot.resting) this.patch({ cursor: point, resting: true });
  }

  clear(): void {
    this.advanceAnimationToken();
    this.patch({
      visible: false,
      cursorVisible: false,
      clicking: false,
      fieldRect: null,
      scrollDirection: null,
    });
  }

  private async animateCursor(
    from: { x: number; y: number },
    to: { x: number; y: number },
    signal?: AbortSignal,
    onStep?: (cursor: AppOperatorPoint) => void
  ): Promise<number | null> {
    const token = this.advanceAnimationToken();
    const duration = appOperatorMotionDurationMs({ from, to });
    this.patch({
      visible: true,
      cursorVisible: true,
      fieldRect: null,
      scrollDirection: null,
      cursor: from,
    });
    const completed = await new Promise<boolean>(resolve => {
      let frame: number;
      const finish = (completed: boolean) => {
        cancelAnimationFrame(frame);
        signal?.removeEventListener('abort', abort);
        resolve(completed);
      };
      const abort = () => finish(false);
      signal?.addEventListener('abort', abort, { once: true });
      const startedAt = performance.now();
      const tick = (now: number) => {
        if (token !== this.animationToken) {
          finish(false);
          return;
        }
        const progress = Math.min(1, (now - startedAt) / duration);
        // What the cursor carries goes straight to where it is let go: a drawn line stays a line.
        const cursor = onStep
          ? pointBetween(from, to, progress)
          : createNaturalAppOperatorPath({ from, to, progress });
        this.patch({ cursor });
        onStep?.(cursor);
        if (progress < 1) {
          frame = requestAnimationFrame(tick);
          return;
        }
        finish(true);
      };
      frame = requestAnimationFrame(tick);
    });
    return completed ? token : null;
  }

  private sleep(ms: number, token: number): Promise<void> {
    if (!this.isTokenActive(token)) {
      return Promise.resolve();
    }
    return new Promise(resolve => {
      let settled = false;
      let timeoutId: number | undefined;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId);
        }
        this.pendingDelayCancels.delete(finish);
        resolve();
      };
      this.pendingDelayCancels.add(finish);
      timeoutId = window.setTimeout(finish, ms);
    });
  }

  private patch(patch: Partial<AIOperatorPresenceSnapshot>): void {
    // Whatever is shown next ends the rest; only resting itself keeps it.
    this.snapshot = { ...this.snapshot, resting: false, ...patch };
    for (const listener of this.listeners) listener();
  }

  private isTokenActive(token: number): boolean {
    return token === this.animationToken;
  }

  private advanceAnimationToken(): number {
    this.animationToken++;
    const cancels = Array.from(this.pendingDelayCancels);
    this.pendingDelayCancels.clear();
    for (const cancel of cancels) cancel();
    return this.animationToken;
  }
}

const stores = new Map<string, AIOperatorPresentationStore>();

export function getAIOperatorPresentationStore(
  appBaseId: string
): AIOperatorPresentationStore {
  let store = stores.get(appBaseId);
  if (!store) {
    store = new AIOperatorPresentationStore();
    stores.set(appBaseId, store);
  }
  return store;
}
